import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Routes, Route, Navigate } from "react-router-dom";
import { layoutTypes } from "./constants/layout";
// Import Routes all
import { authProtectedRoutes, publicRoutes } from "./routes";
import {
  createMenuConfig,
  flattenMenuRoutesWithPermission,
  normalizeMenuRoutePath,
  resolveRoutePermission,
} from "helpers/appSidebarMenu";
import { usePermissionContext } from "libs/context/PermissionContext";

// Import all middleware
import Authmiddleware from "./routes/route";

// layouts Format
import VerticalLayout from "./components/VerticalLayout/";
import HorizontalLayout from "./components/HorizontalLayout/";
import NonAuthLayout from "./components/NonAuthLayout";

// Import scss
import "./assets/scss/theme.scss";
import "./assets/scss/common.scss";
import 'react-toastify/dist/ReactToastify.css';

import fakeBackend from "./helpers/AuthType/fakeBackend";
import { ToastContainer } from 'react-toastify';
import { startNotificationHub } from 'libs/signalr';
import LiveNotificationAlert from 'components/Common/LiveNotificationAlert';
import DocumentTitle from 'components/Common/DocumentTitle';
import { useEnableBrowserAutocomplete } from 'hooks/useEnableBrowserAutocomplete';
import { useDraggableModals } from 'hooks/useDraggableModals';
import { useResizableModals } from 'hooks/useResizableModals';

// Activating fake backend
fakeBackend();

const getLayout = (layoutType) => {
  let Layout = VerticalLayout;
  switch (layoutType) {
    case layoutTypes.VERTICAL:
      Layout = VerticalLayout;
      break;
    case layoutTypes.HORIZONTAL:
      Layout = HorizontalLayout;
      break;
    default:
      break;
  }
  return Layout;
};

const PermissionRouteGuard = ({ routePath, children }) => {
  const { permissionData, loading, initialized } = usePermissionContext() || {};
  const routePermissionMap = useMemo(
    () => flattenMenuRoutesWithPermission(createMenuConfig(permissionData)),
    [permissionData]
  );

  const normalizedPath = normalizeMenuRoutePath(routePath);
  if (normalizedPath === "no-permission" || normalizedPath === "access-denied") {
    return children;
  }

  if (!initialized) return null;
  if (loading && typeof permissionData === "undefined") return null;

  const { guarded, allowed } = resolveRoutePermission(
    routePath,
    routePermissionMap
  );

  if (guarded && !allowed) {
    return <Navigate to="/no-permission" replace />;
  }

  return children;
};

PermissionRouteGuard.propTypes = {
  routePath: PropTypes.string,
  children: PropTypes.node,
};

const App = () => {
  const authUser = localStorage.getItem("authUser");

  useEnableBrowserAutocomplete();
  useDraggableModals();
  useResizableModals();

  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
  
    if (authUser) {
      startNotificationHub();
    }
  }, [authUser]);

  const LayoutProperties = createSelector(
    (state) => state.Layout,
    (layout) => ({
      layoutType: layout.layoutType,
    })
  );

  const {
    layoutType
  } = useSelector(LayoutProperties);

  const Layout = getLayout(layoutType);

  return (
    <React.Fragment>
      <DocumentTitle />
      <Routes>
        {publicRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <NonAuthLayout>
                {route.component}
              </NonAuthLayout>
            }
            key={idx}
            exact={true}
          />
        ))}

        {authProtectedRoutes.map((route, idx) => (
          <Route
            path={route.path}
            element={
              <Authmiddleware>
                <PermissionRouteGuard routePath={route.path}>
                  <Layout>{route.component}</Layout>
                </PermissionRouteGuard>
              </Authmiddleware>}
            key={idx}
            exact={true}
          />
        ))}
      </Routes>
      <ToastContainer />
      <LiveNotificationAlert />
    </React.Fragment>
  );
};

App.propTypes = {
  layout: PropTypes.any
};

export default App;
